import {
  createContext, useContext, useEffect, useRef, useSyncExternalStore,
  type ReactNode,
} from "react";
import { apiClient, apiErrorMessage } from "@/api/client";
import { onConsoleConfigurationChanged } from "@/api/read-invalidation";
import type {
  StorageDetailResponse, StorageReadinessResponse, StorageRuntimeStatusBatchResponse,
  StorageRuntimeStatusSnapshot, StorageSummary,
} from "@/types/api";

type StorageConfiguration = StorageSummary | StorageDetailResponse | StorageReadinessResponse;
type ListResponse = {
  items: StorageSummary[];
  nextCursor?: string | null;
};
type Resource<T> = {
  value: T | null;
  loading: boolean;
  error: string | null;
};
const failedRuntime = (): StorageRuntimeStatusSnapshot => ({
  runtimeStatusState: "unknown",
  freshnessState: "unknown",
  reasonCode: "runtime_status_fetch_failed",
  retryable: true,
  observedAt: null,
  expiresAt: null,
  source: "pf_unavailable",
});
const missingRuntime = (): StorageRuntimeStatusSnapshot => ({
  ...failedRuntime(),
  runtimeStatusState: "not_requested",
  freshnessState: "not_requested",
  reasonCode: "runtime_status_not_returned",
  source: "not_requested",
});

/** Display state owned by one mounted organization frame; never persisted or used for authorization. */
export class ConsoleReadState {
  private revision = 0;
  private sequence = 0;
  private generation = 0;
  private listeners = new Set<() => void>();
  private configurations = new Map<string, {
    sequence: number;
    value: StorageConfiguration;
  }>();
  private runtimes = new Map<string, StorageRuntimeStatusSnapshot>();
  private requests = new Map<string, Promise<unknown>>();
  private resources = new Map<string, Resource<unknown>>();
  private resourceSequences = new Map<string, number>();
  private resourceLoaders = new Map<string, () => Promise<unknown>>();
  private watches = new Map<symbol, {
    ids: string[];
    reload: () => Promise<unknown>;
  }>();
  private interval: ReturnType<typeof setInterval> | null = null;
  private expiry: ReturnType<typeof setTimeout> | null = null;
  private runtimeRequest: Promise<void> | null = null;
  private runtimeQueued = false;

  connect = () => onConsoleConfigurationChanged(read => {
    if (read === "storages") {
      for (const key of this.requests.keys()) {
        if (key.startsWith("configuration:")) this.requests.delete(key);
      }
      void Promise.allSettled([...this.watches.values()].map(watch => watch.reload()));
    } else {
      const key = read;
      const load = this.resourceLoaders.get(key);
      if (load) {
        this.requests.delete(key);
        void this.readResource(key, load);
      }
    }
  });
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };
  snapshot = () => this.revision;
  private emit() {
    this.revision++;
    this.listeners.forEach(listener => listener());
  }

  private async request<T>(key: string, load: () => Promise<T>): Promise<T> {
    const existing = this.requests.get(key);
    if (existing) return existing as Promise<T>;
    const request = load();
    this.requests.set(key, request);
    try {
      return await request;
    } finally {
      if (this.requests.get(key) === request) this.requests.delete(key);
    }
  }

  configuration(id: string) { return this.configurations.get(id)?.value; }
  runtime(id: string) { return this.runtimes.get(id); }
  markRuntimeFailed(id: string) {
    this.runtimes.set(id, failedRuntime());
    this.emit();
  }
  publishConfiguration(value: StorageConfiguration, sequence = ++this.sequence) {
    const old = this.configurations.get(value.storageId);
    if (old && old.sequence > sequence)
      return;
    this.configurations.set(value.storageId, { sequence, value: { ...old?.value, ...value } });
    this.emit();
  }

  readList = (url: string, force = false): Promise<ListResponse> => {
    const key = `configuration:${url}`;
    if (force) this.requests.delete(key);
    return this.request(key, async () => {
      const sequence = ++this.sequence;
      const generation = this.generation;
      const result = await apiClient<ListResponse>(url);
      if (generation === this.generation) {
        result.items.forEach(item => this.publishConfiguration(item, sequence));
        this.queueRuntime();
      }
      return result;
    });
  };

  readDetail = (id: string): Promise<StorageDetailResponse> => this.request(`configuration:/storages/${id}`, async () => {
    const sequence = ++this.sequence;
    const generation = this.generation;
    const result = await apiClient<StorageDetailResponse>(`/storages/${id}`);
    if (generation === this.generation)
      this.publishConfiguration(result, sequence);
    return result;
  });

  readReadiness = (id: string): Promise<StorageReadinessResponse> => this.request(`configuration:/storages/${id}/readiness`, async () => {
    const sequence = ++this.sequence;
    const generation = this.generation;
    const result = await apiClient<StorageReadinessResponse>(`/storages/${id}/readiness`);
    if (generation === this.generation)
      this.publishConfiguration(result, sequence);
    return result;
  });

  watch(ids: string[], reload: () => Promise<unknown>) {
    const token = Symbol();
    this.watches.set(token, { ids, reload });
    this.scheduleExpiry();
    this.queueRuntime();
    if (ids.length && !this.interval) {
      this.interval = setInterval(() => this.queueRuntime(), 15_000);
    }
    return () => {
      this.watches.delete(token);
      if (![...this.watches.values()].some(watch => watch.ids.length)) {
        if (this.interval)
          clearInterval(this.interval);
        if (this.expiry)
          clearTimeout(this.expiry);
        this.interval = null;
        this.expiry = null;
      }
    };
  }

  private queueRuntime() {
    if (!this.watches.size || this.runtimeQueued)
      return;
    this.runtimeQueued = true;
    queueMicrotask(() => {
      this.runtimeQueued = false;
      void this.refreshRuntime();
    });
  }

  refreshRuntime = (): Promise<void> => {
    if (this.runtimeRequest)
      return this.runtimeRequest;
    const ids = [...new Set([...this.watches.values()].flatMap(watch => watch.ids))];
    if (!ids.length)
      return Promise.resolve();
    const generation = this.generation;
    const request = (async () => {
      // The public batch contract permits at most 100 targets; separate visible pages may exceed it.
      for (let offset = 0; offset < ids.length; offset += 100) {
        const targets = ids.slice(offset, offset + 100);
        try {
          const response = await apiClient<StorageRuntimeStatusBatchResponse>("/storages/runtime-status:batch", {
            method: "POST",
            body: JSON.stringify({ storageIds: targets, forceProbe: false }),
          });
          if (generation !== this.generation)
            return;
          const returned = new Map(response.items.map(({ storageId, ...snapshot }) => [storageId, snapshot]));
          targets.forEach(id => this.runtimes.set(id, returned.get(id) ?? missingRuntime()));
        } catch {
          if (generation !== this.generation)
            return;
          targets.forEach(id => this.runtimes.set(id, failedRuntime()));
        }
        this.emit();
        this.scheduleExpiry();
      }
      const recovering = new Set(ids.filter(id => {
        const runtime = this.runtime(id);
        return this.configuration(id)?.settingState === "unknown" &&
          this.configuration(id)?.settingBlockedReason === "platform_unavailable" &&
          runtime?.source === "pf_current" && runtime.runtimeStatusState === "available" &&
          runtime.freshnessState === "fresh" && (runtime.expiresAt === null || Date.parse(runtime.expiresAt) > Date.now());
      }));
      // Re-read configuration, rather than inferring readiness from runtime availability.
      // A stalled configuration read must not stop runtime polling for other consumers.
      void Promise.allSettled([...recovering].map(id => this.readDetail(id)));
    })();
    this.runtimeRequest = request;
    void request.finally(() => {
      if (this.runtimeRequest !== request)
        return;
      this.runtimeRequest = null;
      if (generation === this.generation && [...this.watches.values()].some(watch => watch.ids.some(id => !ids.includes(id))))
        this.queueRuntime();
    });
    return request;
  };

  private scheduleExpiry() {
    if (this.expiry)
      clearTimeout(this.expiry);
    const ids = [...new Set([...this.watches.values()].flatMap(watch => watch.ids))];
    const deadlines = ids.map(id => Date.parse(this.runtime(id)?.expiresAt ?? "")).filter(time => Number.isFinite(time) && time > Date.now());
    if (deadlines.length) {
      this.expiry = setTimeout(() => {
        this.emit();
        this.scheduleExpiry();
      }, Math.min(Math.max(1, Math.min(...deadlines) - Date.now()), 2_147_483_647));
    }
  }

  resource<T>(key: string): Resource<T> { return (this.resources.get(key) ?? { value: null, loading: true, error: null }) as Resource<T>; }
  publishResource<T>(key: string, value: T, sequence = ++this.sequence) {
    if ((this.resourceSequences.get(key) ?? 0) > sequence)
      return;
    this.resourceSequences.set(key, sequence);
    this.resources.set(key, { value, loading: false, error: null });
    this.emit();
  }

  readResource<T>(key: string, load: () => Promise<T>): Promise<boolean> {
    this.resourceLoaders.set(key, load);
    return this.request(key, async () => {
      const generation = this.generation;
      const sequence = ++this.sequence;
      this.resourceSequences.set(key, sequence);
      this.resources.set(key, { ...this.resource(key), loading: true, error: null });
      this.emit();
      try {
        const value = await load();
        if (generation === this.generation)
          this.publishResource(key, value, sequence);
        return true;
      } catch (error) {
        if (generation === this.generation && this.resourceSequences.get(key) === sequence) {
          this.resources.set(key, { value: null, loading: false, error: apiErrorMessage(error) });
          this.emit();
        }
        return false;
      }
    });
  }

  dispose() {
    this.generation++;
    if (this.interval)
      clearInterval(this.interval);
    if (this.expiry)
      clearTimeout(this.expiry);
    this.interval = null;
    this.expiry = null;
    this.runtimeRequest = null;
    this.requests.clear();
    this.watches.clear();
    this.configurations.clear();
    this.runtimes.clear();
    this.resources.clear();
    this.resourceSequences.clear();
    this.resourceLoaders.clear();
  }
}

const Context = createContext<ConsoleReadState | null>(null);
export function ConsoleReadStateProvider({ children }: {
  children: ReactNode;
}) {
  const [state] = useStateValue();
  useEffect(() => {
    const disconnect = state.connect();
    return () => { disconnect(); state.dispose(); };
  }, [state]);
  return <Context.Provider value={state}>{children}</Context.Provider>;
}

function useStateValue() {
  const state = useRef<ConsoleReadState>();
  if (!state.current)
    state.current = new ConsoleReadState();
  return [state.current] as const;
}

export function useConsoleReadState() {
  const shared = useContext(Context);
  const [local] = useStateValue();
  const state = shared ?? local;
  useEffect(() => {
    if (shared)
      return;
    const disconnect = local.connect();
    return () => { disconnect(); local.dispose(); };
  }, [shared, local]);
  useSyncExternalStore(state.subscribe, state.snapshot, state.snapshot);
  return state;
}
